<template>
  <nav class="navbar">
    <div class="nav-bar-logo">
      <img src="../store/new/logo.png" class="nav-bar-logo-img" alt="网站logo">
    </div>
    <div class="navbar-container">
      <ul class="navbar-menu main-menu">
        <li v-for="item in mainMenuItems" :key="item.id"
          :class="{ dropdown: item.children, 'dropdown-open': openDropdown === item.id }"
          @mouseenter="openDropdown = item.children ? item.id : null" @mouseleave="openDropdown = null">
          <router-link v-if="item.route" :to="item.route" class="menu-link" :class="{ active: isActive(item.route) }">
            {{ item.name }}
          </router-link>
        </li>
      </ul>
      <ul class="navbar-menu right-menu">
        <li v-for="item in rightMenuItems" :key="item.id">
          <div v-if="item.id === 6" class="search-container" @click.stop="toggleSearch">
            
            <template v-if="!isSearchOpen">
              <img :src="item.img" alt="搜索" class="search-icon">
            </template>
            <template v-else>
              <input 
                type="text" 
                class="search-input" 
                placeholder="搜索内容..." 
                v-focus="isSearchOpen"
      
              >
            </template>
          </div>
          <router-link v-else-if="item.route" :to="item.route" class="menu-link"
            :class="{ active: isActive(item.route) }">
            {{ item.name }}
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const openDropdown = ref(null)
const isSearchOpen = ref(false)

// 聚焦指令
const vFocus = { 
  mounted(el) {
    el.focus()
  }
}

// 左侧主菜单
const mainMenuItems = ref([
  { id: 1, name: '首页', route: '/' },
  { id: 2, name: '补己探索', route: '/products' },
  { id: 3, name: '补己家族', route: '/buy' },
  { id: 4, name: '补己动态', route: '/dynamic' },
  { id: 5, name: '加入补己', route: '/join' }
])

// 右侧功能菜单
const rightMenuItems = ref([
  {
    id: 6,
    name: '搜索',
    route: '/search',
    img: "https://youke1.picui.cn/s1/2025/08/27/68af14255d181.png"
  },
  { id: 7, name: '加入会员', route: '/member' }
])

// 路由激活判断
const isActive = (routePath) => {
  return route.path === routePath
}

// 搜索功能
const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value
}

const handleSearch = (e) => {
  const searchTerm = e.target.value.trim()
  if (searchTerm) {
    router.push({ path: '/search', query: { q: searchTerm } })
    isSearchOpen.value = false
  }
}

// 点击外部关闭搜索框
const handleClickOutside = (e) => {
  const searchContainer = document.querySelector('.search-container')
  if (searchContainer && !searchContainer.contains(e.target)) {
    isSearchOpen.value = false
  }
}

// 响应式判断
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 900
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
$primary-color: #51423B;
$active-color: #D4AF37;
$text-color: white;
$dropdown-bg: #1a1a1a;
$transition-speed: 0.36s;

.nav-bar-logo-img {
  height: 70px;
}

.navbar {
  background: $primary-color;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 100px;
  align-items: center;
  display: flex;
  padding: 0 30px;
  box-sizing: border-box;
  min-width: 1400px; /* 外层最小宽度 */

  &-container {
    width: 100%;
    min-width: 1400px; /* 关键修复：与外层最小宽度同步 */
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &-menu {
    list-style: none;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    margin-left: 80px;

    > li {
      position: relative;
    }

    &.main-menu {
      gap: 35px;
    }

    &.right-menu {
      gap: 20px;
    }
  }
}

.menu-link {
  color: $text-color;
  text-decoration: none;
  font-size: 20px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;
  padding-bottom: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    height: 2px;
    background: $text-color;
    display: inline-block;
    position: absolute;
    bottom: -6px;
    width: 0;
    left: 50%;
    transition: width $transition-speed ease, left $transition-speed ease;
  }

  &:hover::before {
    width: 100%;
    left: 0;
  }

  &.active {
    color: $active-color;

    &::before {
      width: 100%;
      left: 0;
      background: $active-color;
    }
  }
}

.navbar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 8px 16px;
  margin-right: 20px;
}

// 下拉菜单样式
.dropdown-open {
  .dropdown-menu {
    display: block;
  }
}

.dropdown-menu {
  display: none;
  position: absolute;
  left: -60px;
  top: calc(100% + 55px);
  background: $dropdown-bg;
  min-width: 280px;
  border: 1px solid rgba($active-color, 0.3);
  border-radius: 4px;
  padding: 6px 0;
  z-index: 10;
  list-style: none;
  transition: opacity 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: -55px;
    left: 0;
    right: 0;
    height: 55px;
    background: transparent;
  }

  li {
    width: 100%;
    list-style: none;
  }
}

.dropdown-link {
  display: block;
  color: $text-color;
  text-decoration: none;
  font-size: 1.5rem;
  text-align: center;
  padding: 10px 16px;
  border-radius: 2px;
  transition: all 0.3s ease;
  position: relative;
  padding-bottom: 10px;

  &::before {
    content: '';
    height: 2px;
    background: $text-color;
    display: inline-block;
    position: absolute;
    bottom: -6px;
    width: 0;
    left: 50%;
    transition: width $transition-speed ease, left $transition-speed ease;
  }

  &:hover::before {
    width: 100%;
    left: 0;
  }

  &.active::before {
    width: 100%;
    left: 0;
    background: $active-color;
  }
}

// 搜索相关样式
.search-container {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;

  .search-icon {
    height: 25px;
    width: 25px;
    transition: transform 0.3s ease;
  }

  .search-input {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: $text-color;
    padding: 8px 12px;
    font-size: 16px;
    width: 0;
    opacity: 0;
    transition: width 0.3s ease, opacity 0.3s ease;
    outline: none;

    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &:has(.search-input:not([style*="width: 0px"])) {
    .search-input {
      width: 200px;
      opacity: 1;
      margin-left: 8px;
    }
  }
}

.navbar-container{
  flex: 1;
}

// 小屏幕适配优化
@media (max-width: 1600px) {
  .navbar-menu.main-menu {
    margin-left: 40px; /* 减少左侧间距，避免挤压右侧 */
  }
}
</style>
